import React, { useMemo, memo, useState, useCallback } from "react";
import { useField } from "formik";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import JoinCampaignLocationHeader from "@/features/campaigns/components/joinCampaignCommon/JoinCampaignLocationHeader";
import Feedback from "@/shared/components/feedback/Feedback";
import RRDLocationDistributionCost from "./RRDLocationDistributionCost";
import RRDLocationStoreCopy from "./RRDLocationStoreCopy";
import RRDLocationCouponExpiry from "./RRDLocationCouponExpiry";
import RRDLocationTotalCost from "./RRDLocationTotalCost";
import { rrdDirectMailCampaignHelper } from "@/features/campaigns/direct-mail/rrd/utils/rrdDirectMailHelper";
import RRDDirectMailingDateInput from "../common/rrdInputFields/RRDDirectMailingDateInput";
import RRDTemplate from "../common/rrdTemplate/RRDTemplate";
import RRDMailingDateAvailabilityDays from "../common/rrdInputFields/RRDMailingDateAvailabilityDays";
import LocationHeaderDetails from "../common/LocationHeaderDetails";
import useIsParticipant from "@/features/auth/hooks/useIsParticipant";
import {
  isTemporarilyClosedLocation,
  TemporaryClosedStatusNote,
} from "@/components/muiCustom/note/InlineStatusNote";

const MailingDateAvailabilityDays =
  RRDMailingDateAvailabilityDays as React.ElementType;

type RRDCampaignSingleLocationProps = {
  loc: Record<string, any>;
  shouldExpandIt?: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<any[]>>;
  campaign?: Record<string, any>;
  storeCopyCPMAdmin?: any;
  minCouponDurationAdmin?: any;
  lastCouponExpiryDateAdmin?: any;
  couponExpirationSetBy?: string;
  fixedCouponDurationDays?: number | null;
  distributionCostAdmin?: any;
};

const RRDCampaignSingleLocation = ({
  loc,
  shouldExpandIt,
  setExpanded,
  storeCopyCPMAdmin,
  minCouponDurationAdmin,
  lastCouponExpiryDateAdmin,
  couponExpirationSetBy,
  fixedCouponDurationDays,
  distributionCostAdmin,
}: RRDCampaignSingleLocationProps) => {
  const isParticipant = useIsParticipant();
  const [{ value: locFormData }] = useField(loc.id);

  const isFullyFilledLocation = useMemo(() => {
    return rrdDirectMailCampaignHelper.isFullyFilledLocation(locFormData);
  }, [locFormData]);

  const locationMailingWeeks = useMemo(() => {
    return loc?.mailing_weeks || [];
  }, [loc]);

  const expiryDateConstraints = useMemo(() => {
    return rrdDirectMailCampaignHelper.getExpiryDateConstraints(
      locFormData?.mailing_date,
      minCouponDurationAdmin,
      lastCouponExpiryDateAdmin
    );
  }, [
    locFormData?.mailing_date,
    minCouponDurationAdmin,
    lastCouponExpiryDateAdmin,
  ]);

  const singleLocationAvailabilityData = useMemo(() => {
    return rrdDirectMailCampaignHelper.getSingleLocationAvailabilityData(
      locationMailingWeeks,
      "var(--primary-tint-hover)"
    );
  }, [locationMailingWeeks]);

  const daySlot = useMemo(
    () => (props: Record<string, any>) => (
      <MailingDateAvailabilityDays
        {...props}
        weeklyAvailabilityData={singleLocationAvailabilityData}
      />
    ),
    [singleLocationAvailabilityData]
  );

  const handleOnExpand =
    (panel: any) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        setExpanded((expanded) => [...expanded, panel]);
      } else {
        setExpanded((expanded) => expanded.filter((l) => l !== panel));
        //   onBlur?.();
      }
    };

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const handleOpenFeedback = useCallback(() => setFeedbackOpen(true), []);
  const handleCloseFeedback = useCallback(() => setFeedbackOpen(false), []);

  return (
    <Accordion
      expanded={shouldExpandIt}
      onChange={handleOnExpand(loc.id)}
      sx={{
        background: "var(--neutral-white) 0% 0% no-repeat padding-box",
        boxShadow: "none",
        border: "1px solid var(--neutral-200)",
        borderRadius: "var(--radius-container)",
        marginBottom: "0.5rem!important",
        width: "100%",
      }}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        className="location-header"
        sx={{
          "& .MuiAccordionSummary-content": {
            padding: "0 !important",
            margin: "0 !important",
          },
          ":not(.Mui-expanded):hover": {
            background: "var(--neutral-200)",
            borderRadius: "var(--radius-container)",
          },
        }}
      >
        <JoinCampaignLocationHeader
          locationName={rrdDirectMailCampaignHelper.getLocationName(loc)}
          isFullyFilled={isFullyFilledLocation}
          locationStatusNote={
            isTemporarilyClosedLocation(loc) ? (
              <TemporaryClosedStatusNote location={loc} />
            ) : null
          }
          otherDataToDisplay={
            <LocationHeaderDetails
              mailingDateFieldName={`${loc.id}.mailing_date`}
              frontCouponExpirationDateFieldName={`${loc.id}.front_coupon_expiry_date`}
              backCouponExpirationDateFieldName={`${loc.id}.back_coupon_expiry_date`}
              printType={loc?.print_type}
            />
          }
        />
      </AccordionSummary>

      <AccordionDetails className="location-details">
        <Box>
          <RRDTemplate
            heroImageFieldName={`${loc.id}.hero_images`}
            couponFieldName={`${loc.id}.coupons`}
            frontCouponExpirationDateFieldName={`${loc.id}.front_coupon_expiry_date`}
            backCouponExpirationDateFieldName={`${loc.id}.back_coupon_expiry_date`}
            assets={rrdDirectMailCampaignHelper.getLocationAssets(loc)}
            printType={loc?.print_type}
            layoutKey={loc?.layout_key || loc?.layoutKey}
          >
            <Grid container spacing={2} sx={{ mt: 2, width: "100%" }}>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 3 }} />
                <RRDLocationDistributionCost
                  locationId={loc.id}
                  distributionCostAdmin={distributionCostAdmin}
                />
                <Divider sx={{ my: 3 }} />
                <RRDLocationStoreCopy
                  locationId={loc.id}
                  distributionToggleName={`${loc.id}.distribution`}
                  storeCopyCPMAdmin={storeCopyCPMAdmin}
                  address={`${loc?.street_address}, ${loc?.city}, ${loc?.state}, ${loc?.zip_code}`}
                  onOpenFeedback={
                    isParticipant ? handleOpenFeedback : undefined
                  }
                />
                <Divider sx={{ my: 3 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6, xl: 4 }}>
                    <RRDDirectMailingDateInput
                      name={`${loc.id}.mailing_date`}
                      mailingWeeks={locationMailingWeeks}
                      daySlot={daySlot}
                      minCouponDurationAdmin={minCouponDurationAdmin}
                      lastCouponExpiryDateAdmin={lastCouponExpiryDateAdmin}
                      couponExpirationSetBy={couponExpirationSetBy}
                      fixedCouponDurationDays={fixedCouponDurationDays}
                      frontCouponExpiryDateFieldName={`${loc.id}.front_coupon_expiry_date`}
                      backCouponExpiryDateFieldName={`${loc.id}.back_coupon_expiry_date`}
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: "grow" }}>
                    <RRDLocationCouponExpiry
                      locationId={loc.id}
                      printType={locFormData?.print_type}
                      layoutKey={locFormData?.layout_key}
                      expiryDateConstraints={expiryDateConstraints}
                      minCouponDurationAdmin={minCouponDurationAdmin}
                      couponExpirationSetBy={couponExpirationSetBy}
                      fixedCouponDurationDays={fixedCouponDurationDays}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 4, lg: 12, xl: 4 }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-end",
                    }}
                  >
                    <RRDLocationTotalCost
                      locationId={loc.id}
                      distributionCost={locFormData?.distribution_cost}
                      storeCopyCost={locFormData?.store_copy_cost}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
              </Grid>
            </Grid>
          </RRDTemplate>
        </Box>
      </AccordionDetails>
      <Feedback open={feedbackOpen} handleClose={handleCloseFeedback} />
    </Accordion>
  );
};

export default memo(RRDCampaignSingleLocation);
